import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  CalendarIcon,
  Phone,
  User,
  CheckCircle2,
  ListChecks,
  AlertCircle,
  Upload,
  Loader2,
  X,
  Edit2
} from "lucide-react";

const QuickInfoSection = ({
  lead,
  calls,
  isAdmin,
  pickPoolStatus,
  isPushingToPickPool,
  showPickPoolError,
  setShowPickPoolError,
  handlePushToPickPool,
  setIsEditing,
  formatFieldName,
  isPushingToERP,
  handlePushToERP
}) => {
  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg">Quick Info</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {lead.assigned_to_name && (
          <div className="flex items-center gap-3 text-sm bg-blue-50 p-2 rounded-md">
            <User className="w-4 h-4 text-blue-600" />
            <span className="text-blue-600 font-medium">Assigned to:</span>
            <span className="text-blue-600 font-semibold">{lead.assigned_to_name}</span>
            {lead.allocated_at && (
              <span className="text-blue-500 text-xs">
                (on {new Date(lead.allocated_at).toLocaleDateString()})
              </span>
            )}
          </div>
        )}
        <div className="flex items-center gap-3 text-sm">
          <CalendarIcon className="w-4 h-4 text-slate-400" />
          <span className="text-slate-500">Created:</span>
          <span className="text-slate-900">{new Date(lead.created_at).toLocaleDateString()}</span>
        </div>
        {lead.last_contacted && (
          <div className="flex items-center gap-3 text-sm">
            <Phone className="w-4 h-4 text-slate-400" />
            <span className="text-slate-500">Last Contact:</span>
            <span className="text-slate-900">{new Date(lead.last_contacted).toLocaleDateString()}</span>
          </div>
        )}
        <div className="flex items-center gap-3 text-sm">
          <Phone className="w-4 h-4 text-slate-400" />
          <span className="text-slate-500">Total Calls:</span>
          <span className="text-slate-900">{calls.length}</span>
        </div>
        {lead.region && (
          <div className="flex items-center gap-3 text-sm">
            <span className="text-slate-500">Region:</span>
            <span className="text-slate-900">{lead.region}</span>
          </div>
        )}
        {lead.customer_type && (
          <div className="flex items-center gap-3 text-sm">
            <span className="text-slate-500">Type:</span>
            <span className="text-slate-900">{lead.customer_type}</span>
          </div>
        )}
        
        {/* ERP Status */}
        {lead.erp_pushed && (
          <div className="flex items-center gap-3 text-sm bg-emerald-50 p-2 rounded-md">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span className="text-emerald-600 font-medium">Pushed to ERP</span>
            {lead.erp_id && <span className="text-emerald-900 font-mono text-xs">ID: {lead.erp_id}</span>}
          </div>
        )}

        {/* Pick Pool Status & Button */}
        <div className="mt-4 border-t pt-4">
          <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <ListChecks className="w-4 h-4" />
            Pick Pool Status
          </h4>
          
          {lead.pick_pool_ready ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 text-sm bg-green-50 p-3 rounded-md">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="text-green-700 font-medium">In Pick Pool</span>
              </div>
              {lead.pushed_to_pick_pool_by && (
                <p className="text-xs text-slate-500">Pushed by: {lead.pushed_to_pick_pool_by}</p>
              )}
            </div>
          ) : isAdmin ? (
            <div className="flex items-center gap-2 text-sm bg-slate-50 p-3 rounded-md">
              <AlertCircle className="w-5 h-5 text-slate-400" />
              <span className="text-slate-600">Not yet pushed to Pick Pool</span>
            </div>
          ) : (
            <>
              {pickPoolStatus && (
                <div className="mb-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Fields completed:</span>
                    <span className={`font-medium ${pickPoolStatus.missing_count === 0 ? 'text-green-600' : 'text-orange-600'}`}>
                      {pickPoolStatus.total_required - pickPoolStatus.missing_count} / {pickPoolStatus.total_required}
                    </span>
                  </div>
                  {pickPoolStatus.missing_count > 0 && (
                    <div className="mt-2 text-xs text-orange-600">
                      {pickPoolStatus.missing_count} mandatory field(s) missing
                    </div>
                  )}
                  {pickPoolStatus.call_log_required && (
                    <div className="mt-2 text-xs text-rose-600 flex items-start gap-1" data-testid="pick-pool-call-required">
                      <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                      <span>Call log required before push — log a call first.</span>
                    </div>
                  )}
                </div>
              )}
              
              <Button
                onClick={handlePushToPickPool}
                disabled={isPushingToPickPool}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="push-to-pick-pool-btn"
              >
                {isPushingToPickPool ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Checking...
                  </>
                ) : (
                  <>
                    <ListChecks className="w-4 h-4 mr-2" />
                    Push to Pick Pool
                  </>
                )}
              </Button>
            </>
          )}
        </div>

        {/* Pick Pool Error Dialog */}
        <Dialog open={showPickPoolError} onOpenChange={setShowPickPoolError}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertCircle className="w-5 h-5" />
                Cannot Push to Pick Pool
              </DialogTitle>
              <DialogDescription>
                {(pickPoolStatus?.missing_fields?.length || 0) > 0
                  ? "The following mandatory fields are missing:"
                  : "The mandatory fields are all filled, but the following business rules are blocking the push:"}
              </DialogDescription>
            </DialogHeader>
            
            <div className="mt-4 max-h-60 overflow-y-auto">
              <div className="space-y-2">
                {pickPoolStatus?.missing_fields?.map((field) => (
                  <div 
                    key={field} 
                    className="flex items-center gap-2 p-2 bg-red-50 rounded text-sm text-red-700"
                  >
                    <X className="w-4 h-4 text-red-500" />
                    <span>{formatFieldName(field)}</span>
                  </div>
                ))}
                {/* Business-rule blockers — no missing fields but still not ready */}
                {pickPoolStatus?.name_company_same && (
                  <div className="flex items-center gap-2 p-2 bg-red-50 rounded text-sm text-red-700">
                    <X className="w-4 h-4 text-red-500" />
                    <span>Contact Person and Company Name are identical — please differentiate them.</span>
                  </div>
                )}
                {(pickPoolStatus?.call_log_count ?? 1) === 0 && (
                  <div className="flex items-center gap-2 p-2 bg-red-50 rounded text-sm text-red-700">
                    <X className="w-4 h-4 text-red-500" />
                    <span>At least one call log is required before pushing to Pick Pool.</span>
                  </div>
                )}
              </div>
            </div>
            
            <DialogFooter className="mt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowPickPoolError(false)}
              >
                Close
              </Button>
              <Button 
                onClick={() => {
                  setShowPickPoolError(false);
                  setIsEditing(true);
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit Lead
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Push to ERP Button */}
        {(lead.status === 'qualified' || lead.status === 'converted_to_lfb') && !lead.erp_pushed && (
          <Button
            onClick={handlePushToERP}
            disabled={isPushingToERP}
            className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="push-to-erp-btn"
          >
            {isPushingToERP ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Pushing to ERP...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Push to ERP
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default QuickInfoSection;
